import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { style } from './image.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'

@customElement('csc-product-image')
export class CSCProductImage extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String })
  source: string = ''

  @property({ type: String, attribute: 'fallback-source' })
  fallbackSource: string = ''

  @property({ type: String, attribute: 'secondary-source' })
  secondarySource: string | null = null

  @property({ type: String })
  alt: string | null = null

  @property({ type: Number })
  width: number = 0

  @property({ type: Number })
  height: number = 0

  @property({ type: Number, attribute: 'width-mobile' })
  widthMobile: number | null = null

  @property({ type: Number, attribute: 'height-mobile' })
  heightMobile: number | null = null

  @property({ type: Number, attribute: 'width-desktop' })
  widthDesktop: number | null = null

  @property({ type: Number, attribute: 'height-desktop' })
  heightDesktop: number | null = null

  @property({ type: String })
  url: string = ''

  @property({ type: String })
  fit: 'cover' | 'contain' = 'cover'

  @property({ type: String, attribute: 'blend-mode' })
  blendMode: 'none' | 'multiply' = 'none'

  @query('img')
  image!: HTMLImageElement

  @state()
  private _isDesktop = false

  @state()
  private _isHovering = false

  private _mediaQuery: MediaQueryList | null = null

  numberOfImageFetchingRetries: number = 0

  override connectedCallback() {
    super.connectedCallback()
    this._mediaQuery = window.matchMedia(`(min-width: ${breakpoints.medium}px)`)
    this._isDesktop = this._mediaQuery.matches
    this._mediaQuery.addEventListener('change', this._onMediaChange)
    this.addEventListener('mouseenter', this._onMouseEnter)
    this.addEventListener('mouseleave', this._onMouseLeave)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this._mediaQuery?.removeEventListener('change', this._onMediaChange)
    this.removeEventListener('mouseenter', this._onMouseEnter)
    this.removeEventListener('mouseleave', this._onMouseLeave)
  }

  private _onMediaChange = (e: MediaQueryListEvent) => {
    this._isDesktop = e.matches
  }

  private _onMouseEnter = () => {
    if (this.secondarySource) this._isHovering = true
  }

  private _onMouseLeave = () => {
    this._isHovering = false
  }

  private get _activeSource(): string {
    return (this._isHovering && this.secondarySource) ? this.secondarySource : this.source
  }

  private get _activeWidth(): number | undefined {
    if (this.widthDesktop !== null || this.widthMobile !== null) {
      return (this._isDesktop ? this.widthDesktop : this.widthMobile) ?? undefined
    }
    return this.width || undefined
  }

  private get _activeHeight(): number | undefined {
    if (this.heightDesktop !== null || this.heightMobile !== null) {
      return (this._isDesktop ? this.heightDesktop : this.heightMobile) ?? undefined
    }
    return this.height || undefined
  }

  private get _activeFit(): string | undefined {
    return this._activeWidth === undefined && this._activeHeight === undefined ? this.fit : undefined
  }

  protected override firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties)
  }

  override render() {
    const imageHtml = html`
      <cse-image
        class="${classMap({ 'blend-multiply': this.blendMode === 'multiply' })}"
        source="${this._activeSource}"
        alt="${this.alt ?? nothing}"
        loading="lazy"
        fallback-source="${this.fallbackSource}"
        width="${ifDefined(this._activeWidth)}"
        height="${ifDefined(this._activeHeight)}"
        fit="${ifDefined(this._activeFit)}"
      ></cse-image>
    `

    return html`
      ${this.url 
        ? html`
          <a href="${this.url}">
            ${imageHtml}
          </a>
        `
        : imageHtml
      }
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-image': CSCProductImage
  }
}
